import { useState, useCallback, useRef } from 'react';
import { csrfFetch } from '../utils/csrfFetch';
import { BOOTSTRAP_MODEL_ID } from './useModelCatalog';
import { DEFAULT_MAX_TOKENS } from '../utils/chatSettings';
import { createSseParser } from '../utils/sseParser';

/**
 * useStreamingChat - Hook for handling SSE streaming chat with Claude API
 *
 * Features:
 * - Establishes SSE connection for streaming responses
 * - Progressive text display as chunks arrive
 * - Error handling and reconnection support
 * - Abort capability for stopping generation
 */
export function useStreamingChat() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  /**
   * Send a message and stream the response
   * @param {Object} params - Chat parameters
   * @param {Array} params.messages - Array of {role, content} messages
   * @param {string} params.model - Model ID to use
   * @param {number} params.maxTokens - Max tokens for response
   * @param {string} params.system - System prompt
   * @param {boolean} params.thinkingEnabled - Enable extended thinking mode
   * @param {number} params.thinkingBudget - Token budget for thinking
   * @param {Function} params.onChunk - Callback for each text chunk
   * @param {Function} params.onThinkingChunk - Callback for thinking text chunks
   * @param {Function} params.onComplete - Callback when streaming completes
   * @param {Function} params.onError - Callback for errors
   * @param {Function} params.onStart - Callback when streaming starts
   */
  const sendMessage = useCallback(async ({
    messages,
    model = BOOTSTRAP_MODEL_ID,
    maxTokens = DEFAULT_MAX_TOKENS,
    system,
    thinkingEnabled = false,
    thinkingBudget = 10000,
    agentName,     // DOS-107: optional agent name for agent-mode chat
    conversationId, // the server saves the answer to it when the turn finishes
    onChunk,
    onThinkingChunk,
    onComplete,
    onError,
    onStart,
    onToolCall,       // DOS-76: (toolName: string) => void — fires when Claude invokes a tool
    onQuotaWarning,   // (quotaData) => void — fires when platform key quota is approaching limit
    onThinkingStatus, // ({state:'thinking'|'done', thinkingMs?}) => void — indicator mode (TODO #86)
  }) => {
    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setIsStreaming(true);
    setError(null);

    // Generate a temporary ID for the streaming message
    const tempMessageId = `streaming-${Date.now()}`;
    setStreamingMessageId(tempMessageId);

    try {
      // Make the streaming request
      const response = await csrfFetch('/api/claude/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          messages,
          model,
          maxTokens,
          system,
          thinkingEnabled,
          thinkingBudget,
          ...(agentName ? { agentName } : {}),
          ...(conversationId ? { conversationId } : {}),
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error ${response.status}`);
      }

      // Read the SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let fullThinking = '';
      let completed = false;

      const parser = createSseParser((type, data) => {
        // Handle different event types
        switch (type) {
          case 'start':
            // Stream started
            break;

          case 'thinking':
            // Extended thinking content
            if (data.text) {
              fullThinking += data.text;
              onThinkingChunk?.({
                text: data.text,
                fullThinking,
                messageId: tempMessageId
              });
            }
            break;

          case 'thinking_status':
            // Indicator mode: thinking started / finished; no text is sent.
            onThinkingStatus?.(data);
            break;

          case 'chunk':
            if (data.text) {
              fullContent += data.text;
              onChunk?.({
                text: data.text,
                fullContent,
                messageId: tempMessageId
              });
            }
            break;

          case 'complete':
            // Stream completed with usage stats
            completed = true;
            onComplete?.({
              content: fullContent,
              thinking: fullThinking || data.thinking || null,
              usage: data.usage,
              finishReason: data.finishReason,
              costEstimate: data.costEstimate,
              thinkingMs: data.thinkingMs ?? null,
              // The turn's tool calls, kept server-side; saved on the
              // assistant message and sent back with history.
              toolTraceId: data.toolTraceId ?? null,
              // Set when the server saved the answer (conversationId given).
              savedMessageId: data.messageId ?? null,
              messageId: tempMessageId
            });
            break;

          case 'tool_call':
            // Agent tool invocation (DOS-76)
            if (data.name) {
              onToolCall?.({ name: data.name, input: data.input });
            }
            break;

          case 'quota_warning':
            // Platform key quota approaching limit
            onQuotaWarning?.(data);
            break;

          case 'error':
            // Use the detailed message if available (e.g., quota exceeded includes action guidance)
            throw new Error(data.message || data.error || 'Stream error');

          case 'done':
            // Final event
            break;
        }
      });

      onStart?.({ messageId: tempMessageId, model });

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        parser.push(decoder.decode(value, { stream: true }));
      }

      // The connection closed with no `complete` event. Keep what arrived
      // rather than dropping it, and say it may be cut short.
      if (!completed && fullContent) {
        console.warn('Stream ended without a complete event; keeping the partial response');
        onComplete?.({
          content: `${fullContent}\n\n*[The connection closed before the response finished]*`,
          thinking: fullThinking || null,
          usage: null,
          finishReason: 'connection_closed',
          costEstimate: null,
          thinkingMs: null,
          toolTraceId: null,
          savedMessageId: null,
          messageId: tempMessageId
        });
      }

    } catch (err) {
      // Don't report aborted requests as errors
      if (err.name === 'AbortError') {
        return { aborted: true };
      }

      const errorMessage = err.message || 'Failed to stream response';
      setError(errorMessage);
      onError?.(errorMessage);
      throw err;

    } finally {
      setIsStreaming(false);
      setStreamingMessageId(null);
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Stop the current streaming response
   */
  const stopStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    sendMessage,
    stopStreaming,
    isStreaming,
    streamingMessageId,
    error
  };
}

export default useStreamingChat;
